/**
 * One claim: where it is, what can be done to it, and everything already done.
 *
 * The action buttons are generated entirely from the `available` transitions
 * the API returns — never from a list held here. That is deliberate. The state
 * machine in `apps/web/src/claims/lifecycle.ts` is the authority on what is
 * legal from a given status, and a hardcoded button that the server then
 * refuses teaches an operator to click hopefully rather than deliberately.
 * Adding a transition server-side should make it appear here with no change to
 * this file.
 *
 * Each transition's `meaning` is rendered as helper text because it is not
 * decoration: it states what the operator is asserting. "Recovery verified"
 * means the money is visible in Finance, not that TikTok approved the claim,
 * and an invoice at 15% is raised off the back of it.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import {
  getClaim,
  getClaimHistory,
  transitionClaim,
  type AvailableTransition,
  type ClaimEventRow,
  type OpsClaim,
  type TransitionInput,
} from '../api.js';
import { useAsync } from '../useAsync.js';
import {
  AmountError,
  formatDate,
  formatMinor,
  formatTimestamp,
  humanize,
  NO_VALUE,
  parseMinor,
  pluralize,
} from '../format.js';
import {
  Badge,
  Callout,
  ErrorCallout,
  Field,
  Loading,
  StatusBadge,
  tierClass,
  UrgencyCell,
} from '../ui.js';

/**
 * TikTok's stated refusal reason, as an enumerated code.
 *
 * This is a select and never a free-text box for two separate reasons. The
 * database column is an enum, so an unrecognised string is not a validation
 * error the operator can fix — it is a 500. And structured outcomes are the
 * whole point of recording a rejection: prose cannot tell Phase 5 which claim
 * classes are worth filing at all. Free text belongs in the notes beside it.
 *
 * Mirrors `rejectionReasonCodeEnum` in `packages/db/src/schema.ts`. That list is
 * provisional and expected to grow once real refusals arrive; when it does,
 * this must grow with it or the new codes are unreachable from the console.
 */
const REJECTION_REASONS: readonly { value: string; label: string }[] = [
  { value: 'outside_claim_window', label: 'Outside the claim window' },
  { value: 'insufficient_evidence', label: 'Insufficient evidence' },
  { value: 'invalid_commercial_invoice', label: 'Commercial invoice not accepted' },
  { value: 'duplicate_claim', label: 'Duplicate claim' },
  { value: 'already_reimbursed', label: 'Already reimbursed' },
  { value: 'not_eligible_claim_type', label: 'Claim type not eligible' },
  { value: 'removal_not_tiktok_arranged', label: 'Removal was not TikTok-arranged' },
  { value: 'quantity_mismatch', label: 'Quantity mismatch' },
  { value: 'no_discrepancy_found', label: 'No discrepancy found' },
  { value: 'wrong_clock_date', label: 'Wrong clock date' },
  { value: 'seller_arranged_logistics', label: 'Seller-arranged logistics' },
  { value: 'other', label: 'Other — record TikTok’s wording in the notes' },
];

export function ClaimDetailPage(): ReactNode {
  const { claimId = '' } = useParams();
  const claim = useAsync(() => getClaim(claimId), [claimId]);
  const history = useAsync(() => getClaimHistory(claimId), [claimId]);

  function afterTransition(): void {
    claim.reload();
    history.reload();
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Claim</h1>
          <p className="mono">{claimId}</p>
        </div>
        <Link className="btn" to="/ops/claims">
          Back to open claims
        </Link>
      </div>

      {claim.error ? <ErrorCallout error={claim.error} /> : null}
      {claim.loading && !claim.data ? <Loading label="Loading the claim" rows={3} /> : null}

      {claim.data === null && !claim.loading && !claim.error ? (
        <Callout tone="info" title="This claim is no longer open">
          <p>
            It has reached a terminal state — paid, abandoned or expired — and the API only lists
            claims still occupying their finding&rsquo;s active slot. Its audit trail is below.
          </p>
        </Callout>
      ) : null}

      {claim.data ? <Summary claim={claim.data} /> : null}
      {claim.data ? <Actions claim={claim.data} onDone={afterTransition} /> : null}

      <section className="panel">
        <div className="panel-head">
          <h2>Audit trail</h2>
          <span className="field-hint">
            Append-only, oldest first. Every row names who acted and when.
          </span>
        </div>
        {history.loading && !history.data ? (
          <Loading label="Loading the audit trail" rows={3} />
        ) : null}
        {history.error ? (
          <div className="panel-body">
            <ErrorCallout error={history.error} />
          </div>
        ) : null}
        {history.data ? <Timeline events={history.data.events} /> : null}
      </section>
    </>
  );
}

function Summary({ claim }: { claim: OpsClaim }): ReactNode {
  return (
    <section className={`panel ${tierClass(claim.daysLeft)}`}>
      <div className="panel-body">
        <div className="row row-between">
          <UrgencyCell daysLeft={claim.daysLeft} expiresAt={claim.expiresAt} />
          <StatusBadge status={claim.status} />
        </div>
        <hr className="rule" />
        <dl className="kv">
          <div>
            <dt>Claim code</dt>
            <dd>
              <Badge tone="code">{claim.claimCode}</Badge>
            </dd>
          </div>
          <div>
            <dt>Claimed value</dt>
            <dd>{formatMinor(claim.valueMinor)}</dd>
          </div>
          <div>
            <dt>Seller Center ticket</dt>
            <dd className="mono">
              {claim.ticketId ?? <span className="faint">not filed yet</span>}
            </dd>
          </div>
          <div>
            <dt>Window closes</dt>
            <dd>{claim.expiresAt ? formatDate(claim.expiresAt) : NO_VALUE}</dd>
          </div>
          <div>
            <dt>Refiles</dt>
            <dd>{claim.refileCount}</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}

function Actions({ claim, onDone }: { claim: OpsClaim; onDone: () => void }): ReactNode {
  const [openEvent, setOpenEvent] = useState<string | null>(null);

  if (claim.available.length === 0) {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>Actions</h2>
        </div>
        <Callout tone="info" title="Nothing further is possible">
          <p>
            This status is terminal. Terminal means terminal — the same three states release the
            finding&rsquo;s active-claim slot, so re-pursuing this event means a new finding, not a
            second claim on this one.
          </p>
        </Callout>
      </section>
    );
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>Actions</h2>
        <span className="field-hint">
          Generated from what the lifecycle allows out of <strong>{humanize(claim.status)}</strong>.
        </span>
      </div>
      <div className="panel-body">
        <div className="transition-list">
          {claim.available.map((transition) => (
            <TransitionCard
              key={transition.event}
              claimId={claim.id}
              transition={transition}
              expanded={openEvent === transition.event}
              onExpand={() =>
                setOpenEvent(openEvent === transition.event ? null : transition.event)
              }
              onCollapse={() => setOpenEvent(null)}
              onDone={() => {
                setOpenEvent(null);
                onDone();
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function TransitionCard({
  claimId,
  transition,
  expanded,
  onExpand,
  onCollapse,
  onDone,
}: {
  claimId: string;
  transition: AvailableTransition;
  expanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  onDone: () => void;
}): ReactNode {
  const [ticketId, setTicketId] = useState('');
  const [rejectionReason, setRejectionReason] = useState(REJECTION_REASONS[0]?.value ?? 'other');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const needs = (requirement: string): boolean =>
    transition.requires.includes(requirement as never);
  const needsNotes = needs('reason') || needs('rejectionReason');

  async function submit(): Promise<void> {
    setError(null);
    setAmountError(null);

    const input: { -readonly [K in keyof TransitionInput]: TransitionInput[K] } = {
      event: transition.event,
    };
    if (needs('ticketId')) input.ticketId = ticketId.trim();
    if (needs('rejectionReason')) input.rejectionReason = rejectionReason;
    if (needsNotes && reason.trim()) input.reason = reason.trim();
    if (needs('paidAmount')) {
      try {
        input.paidAmountMinor = parseMinor(amount);
      } catch (caught) {
        setAmountError(caught instanceof AmountError ? caught.message : String(caught));
        return;
      }
    }

    setSubmitting(true);
    try {
      await transitionClaim(claimId, input);
      onDone();
    } catch (caught) {
      // A 422 names what is missing or what is available instead. It is shown
      // unaltered — see `ErrorCallout`.
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  const destructive = transition.event === 'abandon';

  return (
    <div className="transition">
      <strong>{transition.label}</strong>
      <p>{transition.meaning}</p>

      {transition.requires.length > 0 && !expanded ? (
        <span className="field-hint">
          Needs {transition.requires.map(describeRequirement).join(' and ')}.
        </span>
      ) : null}

      {!expanded ? (
        <button
          className={destructive ? 'btn btn-danger' : 'btn btn-primary'}
          onClick={transition.requires.length === 0 ? () => void submit() : onExpand}
          disabled={submitting}
          aria-expanded={transition.requires.length === 0 ? undefined : false}
        >
          {submitting ? 'Recording…' : transition.label}
        </button>
      ) : null}

      {error ? <ErrorCallout error={error} /> : null}

      {expanded ? (
        <div className="transition-form">
          {needs('ticketId') ? (
            <Field
              label="Seller Center ticket id"
              hint="The only proof this was filed. Copy it from the ticket rather than typing it from memory."
            >
              {({ id, describedBy }) => (
                <input
                  id={id}
                  aria-describedby={describedBy}
                  type="text"
                  className="mono"
                  value={ticketId}
                  autoFocus
                  onChange={(event) => setTicketId(event.target.value)}
                />
              )}
            </Field>
          ) : null}

          {needs('rejectionReason') ? (
            <Field
              label="TikTok's stated reason"
              hint="Enumerated on purpose — this is the data that shows which claim classes are worth filing."
            >
              {({ id, describedBy }) => (
                <select
                  id={id}
                  aria-describedby={describedBy}
                  value={rejectionReason}
                  onChange={(event) => setRejectionReason(event.target.value)}
                >
                  {REJECTION_REASONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          ) : null}

          {needs('paidAmount') ? (
            <Field
              label="Amount actually received"
              hint="As it appears in Finance, FBT Billing or the statement adjustment. A partial payment is recorded as a partial payment — do not round it up to the claimed value."
              error={amountError ?? undefined}
            >
              {({ id, describedBy }) => (
                <input
                  id={id}
                  aria-describedby={describedBy}
                  type="text"
                  inputMode="decimal"
                  className="num"
                  value={amount}
                  autoFocus
                  aria-invalid={amountError ? true : undefined}
                  placeholder="1234.56"
                  onChange={(event) => setAmount(event.target.value)}
                />
              )}
            </Field>
          ) : null}

          {needsNotes ? (
            <Field
              label={needs('reason') ? 'Reason' : 'TikTok’s wording (optional)'}
              hint={
                needs('reason')
                  ? 'Recorded on the claim and in the audit trail. The next person to look at this event will only have this.'
                  : 'Paste the refusal text. The enumerated code above is what gets counted; this is what explains it.'
              }
            >
              {({ id, describedBy }) => (
                <textarea
                  id={id}
                  aria-describedby={describedBy}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              )}
            </Field>
          ) : null}

          <div className="form-actions">
            <button
              className={destructive ? 'btn btn-danger' : 'btn btn-primary'}
              onClick={() => void submit()}
              disabled={submitting || !ready()}
            >
              {submitting ? 'Recording…' : `Confirm: ${transition.label}`}
            </button>
            <button className="btn btn-quiet" onClick={onCollapse} disabled={submitting}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );

  /**
   * Client-side gating of the submit button.
   *
   * Deliberately a mirror of the server's requirements and not a replacement
   * for them — the server is the one that refuses, and its 422 explains why in
   * better words than a disabled button ever could. This only avoids a round
   * trip for the obvious cases.
   */
  function ready(): boolean {
    if (needs('ticketId') && !ticketId.trim()) return false;
    if (needs('paidAmount') && !amount.trim()) return false;
    if (needs('reason') && !reason.trim()) return false;
    // "Other" with no wording records nothing anyone can act on later.
    if (needs('rejectionReason') && rejectionReason === 'other' && !reason.trim()) return false;
    return true;
  }
}

function describeRequirement(requirement: string): string {
  switch (requirement) {
    case 'ticketId':
      return 'a Seller Center ticket id';
    case 'rejectionReason':
      return "TikTok's stated reason";
    case 'paidAmount':
      return 'the verified amount received';
    case 'reason':
      return 'a reason';
    default:
      return requirement;
  }
}

function Timeline({ events }: { events: readonly ClaimEventRow[] }): ReactNode {
  if (events.length === 0) {
    return (
      <div className="panel-body">
        <p className="muted">No events recorded against this claim.</p>
      </div>
    );
  }

  return (
    <div className="panel-body">
      <ol className="timeline">
        {events.map((event) => (
          <li key={event.id}>
            <div className="timeline-head">
              <span>{humanize(event.eventType)}</span>
              {event.fromStatus && event.toStatus ? (
                <span className="badge">
                  {event.fromStatus.replace(/_/g, ' ')} → {event.toStatus.replace(/_/g, ' ')}
                </span>
              ) : event.toStatus ? (
                <span className="badge">{event.toStatus.replace(/_/g, ' ')}</span>
              ) : null}
            </div>
            <div className="timeline-meta">
              {event.actorLabel ?? humanize(event.actorType)} · {formatTimestamp(event.occurredAt)}
            </div>
            {event.note ? <p className="timeline-note">{event.note}</p> : null}
          </li>
        ))}
      </ol>
      <p className="field-hint mt-4">
        {pluralize(events.length, 'event')}. This table is append-only in the database; nothing here
        can be edited or removed.
      </p>
    </div>
  );
}
